/**
 * runCapsuleModel — pure derivation for the mini spine: the Chat tab's
 * one live card for a drafting run (a miniature of the Spine), so a run
 * reads as ONE chat item instead of one chat message per drafted section.
 *
 * Turns the run's spine rows (the same scoped ``SpineSection[]`` the Spine
 * tab renders) into a short window: every running row, one row of context
 * either side, and the rest collapsed into "N done" / "N queued" gaps.
 *
 * Once a batch finishes, the card collapses into a one-line ribbon at the
 * same point in the chat. Every batch ("Draft sections", then each "Draft
 * more sections") leaves its own ribbon, so a document's chat keeps one per
 * batch at the moment it ran.
 *
 * Pure: no React, no styling, no I/O.
 *
 * Ported from the sandbox Spine-in-Chat capsule (2026-06-19) onto the
 * launch-film Spine (2026-10-14): rows now carry the whole section so the
 * card can draw the same stage chip and newest action the Spine draws.
 */

import type { SpineSection } from "./hooks/useVariantCRun";
import type { ChatMessage, RunCompleteSummary } from "./messageProjection";
import type { RunState } from "./state";

/** Lifecycle the card shows. Live = running / paused; settled = done / failed. */
export type CapsuleState = "running" | "paused" | "done" | "failed";

/** Map the shell's run state (+ run error) to the card's lifecycle, or null
 *  when there is no run to show. ``post-run`` splits on the run error, the
 *  same signal the Spine's post-run banner uses. */
export function capsuleStateFor(runState: RunState, hasError: boolean): CapsuleState | null {
  switch (runState) {
    case "idle":
      return null;
    case "post-run":
      return hasError ? "failed" : "done";
    case "paused":
    case "pausing":
      return "paused";
    default:
      // mid-run and approval: the run is live.
      return "running";
  }
}

/** A row the Spine draws as live: drafting, or under a regulatory audit. */
export function isRunningSection(s: SpineSection): boolean {
  return s.status === "drafting" || s.auditPhase === "running";
}

/** Rows the window must show: live rows, and rows waiting on the writer. */
function isFocusSection(s: SpineSection): boolean {
  return isRunningSection(s) || s.status === "needs_approval";
}

export type MiniSpineItem =
  | { kind: "row"; section: SpineSection }
  /** Collapsed rows, e.g. "4 done" / "12 queued" / "2 done · 1 queued". */
  | { kind: "gap"; key: string; count: number; label: string };

export interface WindowOptions {
  /** Most focus rows to show before the rest collapse (default 4). The
   *  backend drafts up to three sections at once, plus one approval. */
  maxFocus?: number;
}

/**
 * The index the window centres on when nothing is running: the active
 * section, else the first queued row, else the last row (a settled run).
 */
function restingIndex(sections: readonly SpineSection[], activeSectionId: string | null): number {
  if (activeSectionId) {
    const i = sections.findIndex((s) => s.id === activeSectionId);
    if (i >= 0) return i;
  }
  const q = sections.findIndex((s) => s.status === "queued");
  if (q >= 0) return q;
  return sections.length - 1;
}

function gapLabel(rows: readonly SpineSection[]): string {
  const count = (pred: (s: SpineSection) => boolean) => rows.filter(pred).length;
  const running = count(isRunningSection);
  const queued = count((s) => s.status === "queued");
  const skipped = count((s) => s.status === "skipped");
  const done = rows.length - running - queued - skipped;
  return (
    [
      [done, "done"],
      [running, "drafting"],
      [queued, "queued"],
      [skipped, "skipped"],
    ] as const
  )
    .filter(([n]) => n > 0)
    .map(([n, w]) => `${n} ${w}`)
    .join(" · ");
}

/**
 * Build the mini spine's window. Shows every running / approval row (up to
 * ``maxFocus``), the row just before the first and just after the last, and
 * collapses everything else into gaps, so parallel sections each stay
 * visible without listing the whole run.
 */
export function buildMiniSpineWindow(
  sections: readonly SpineSection[],
  activeSectionId: string | null,
  opts: WindowOptions = {},
): MiniSpineItem[] {
  if (sections.length === 0) return [];
  const maxFocus = opts.maxFocus ?? 4;
  let focus = sections.flatMap((s, i) => (isFocusSection(s) ? [i] : [])).slice(0, maxFocus);
  if (focus.length === 0) focus = [restingIndex(sections, activeSectionId)];
  const shown = new Set<number>(focus);
  const first = focus[0];
  const last = focus[focus.length - 1];
  if (first > 0) shown.add(first - 1);
  if (last < sections.length - 1) shown.add(last + 1);

  const items: MiniSpineItem[] = [];
  let gapStart = 0;
  const pushGap = (end: number) => {
    if (end <= gapStart) return;
    const rows = sections.slice(gapStart, end);
    items.push({ kind: "gap", key: `gap-${gapStart}`, count: rows.length, label: gapLabel(rows) });
  };
  for (let i = 0; i < sections.length; i++) {
    if (!shown.has(i)) continue;
    pushGap(i);
    items.push({ kind: "row", section: sections[i] });
    gapStart = i + 1;
  }
  pushGap(sections.length);
  return items;
}

/** Settled rows that need the writer: failed drafts, flags, approvals. */
export function sectionsNeedingReview(sections: readonly SpineSection[]): SpineSection[] {
  return sections.filter((s) => s.failed || s.status === "flagged" || s.status === "needs_approval");
}

/** Whole-run / whole-section duration: "42s", "3m 05s", "1h 04m" (the
 *  Spine header's format). */
export function formatRunTime(ms: number): string {
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ${String(sec % 60).padStart(2, "0")}s`;
  return `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}m`;
}

// ---------------------------------------------------------------------------
// Batches — one finished ribbon per drafting batch
// ---------------------------------------------------------------------------

/** A batch's bracket in the chat stream: its "Run started" note, or its
 *  run-complete note with the numbers it carries. */
export type BatchMark =
  | { kind: "start"; at: number; key: string; runId?: string | null }
  | { kind: "complete"; at: number; key: string; summary: RunCompleteSummary; runId?: string | null };

/** One drafting batch: a "Draft sections" / "Draft more sections" pass. */
export interface RunBatch {
  key: string;
  /** Where the batch sits in the chat: its start. */
  startAt: number;
  /** Null while the batch has no run-complete note (live, or stopped). */
  endAt: number | null;
  summary: RunCompleteSummary | null;
  /** The run the batch belongs to, when its notes carry one. Batches of one
   *  run ("Draft sections", then "Draft more sections") share it. */
  runId?: string;
}

/**
 * Pair the stream's batch brackets into batches, oldest first. Each start
 * opens a batch; a complete closes the open one. A start that is never
 * closed stays open (the live batch, or one that stopped); a complete with
 * no start in view (its start is on an older, unloaded page) becomes a
 * batch at the complete's own time.
 */
export function pairRunBatches(marks: readonly BatchMark[]): RunBatch[] {
  const batches: RunBatch[] = [];
  let open: RunBatch | null = null;
  for (const mark of [...marks].sort((a, b) => a.at - b.at)) {
    const runId = mark.runId || undefined;
    if (mark.kind === "start") {
      open = { key: `batch-${mark.key}`, startAt: mark.at, endAt: null, summary: null, runId };
      batches.push(open);
    } else if (open) {
      open.endAt = mark.at;
      open.summary = mark.summary;
      open.runId = open.runId ?? runId;
      open = null;
    } else {
      batches.push({ key: `batch-${mark.key}`, startAt: mark.at, endAt: mark.at, summary: mark.summary, runId });
    }
  }
  return batches;
}

/** A finished batch's one-line ribbon text: the headline and the meta. */
export function ribbonText(summary: RunCompleteSummary | null, stopped: boolean): { title: string; meta: string } {
  const plural = (n: number) => (n === 1 ? "section" : "sections");
  const d = summary?.draftedSections;
  const t = summary?.totalSections;
  let title: string;
  if (stopped) title = "Drafting stopped";
  else if (d != null && t != null) title = `Drafted ${d} of ${t} ${plural(t)}`;
  else if (d != null) title = `Drafted ${d} ${plural(d)}`;
  else title = "Drafting finished";
  const meta: string[] = [];
  if (summary?.flaggedSections) meta.push(`${summary.flaggedSections} to review`);
  if (summary?.durationMs != null && summary.durationMs > 0) meta.push(formatRunTime(summary.durationMs));
  return { title, meta: meta.join(" · ") };
}

// ---------------------------------------------------------------------------
// Folding — a batch's own chat rows live inside its card / ribbon
// ---------------------------------------------------------------------------

/**
 * What the fold needs to know about one chat row.
 *
 *   * ``writer`` — the writer's own message. Never folds, and opens a reply:
 *     the next Asthra note or tool row after it answers the writer and stays.
 *   * ``reply`` — an Asthra message that is plainly a reply (an answer, a
 *     proposal, an error, a self-contained Q&A). Never folds; closes a reply.
 *   * ``note`` — an Asthra note that may be run progress ("Study conditions:
 *     1 met…", "Trial context: …").
 *   * ``tool`` — a tool row the run emits (a web search).
 *   * ``other`` — anything else (writer actions, system notes). Never folds.
 */
export type FoldRole = "writer" | "reply" | "note" | "tool" | "other";

export interface FoldProbe {
  role: FoldRole;
  /** The row's run (live entry ``runId`` / persisted ``run_id``), if any. */
  runId: string | null;
}

/**
 * Where each batch's span ends, for folding. A finished batch ends at its
 * complete note. An open one runs to the next batch's start; the live one
 * runs on; a stopped last one ends at the writer's next message (once they
 * are chatting again, the run is over).
 */
export function batchSpanEnds(
  batches: readonly RunBatch[],
  liveKey: string | null,
  writerTimes: readonly number[] = [],
): number[] {
  return batches.map((b, i) => {
    if (b.endAt !== null) return b.endAt;
    const next = batches[i + 1]?.startAt;
    if (next !== undefined) return next;
    if (b.key === liveKey) return Number.POSITIVE_INFINITY;
    const firstWriter = writerTimes.find((t) => t > b.startAt);
    return firstWriter ?? Number.POSITIVE_INFINITY;
  });
}

/**
 * The batch a run-progress row at ``at`` belongs to, or null.
 *
 * A row tagged with a run joins that run's batch whose span holds it
 * (``ends`` from ``batchSpanEnds``). A row with no run id joins only a
 * FINISHED batch, and only strictly inside it. A row tagged with a
 * different run never joins.
 */
export function batchForRow(
  at: number,
  runId: string | null,
  batches: readonly RunBatch[],
  ends: readonly number[] = batchSpanEnds(batches, null),
): RunBatch | null {
  for (let i = batches.length - 1; i >= 0; i--) {
    const b = batches[i];
    if (at < b.startAt) continue;
    if (runId) {
      if (b.runId && b.runId !== runId) continue;
      if (!b.runId && b.endAt === null) continue;
      if (at <= ends[i]) return b;
    } else if (b.endAt !== null && at > b.startAt && at < b.endAt) {
      return b;
    }
  }
  return null;
}

/**
 * Split a chat stream into the rows that stay in chat and the rows each
 * batch folds in (oldest first). Only Asthra notes and tool rows fold,
 * and never the ones answering a writer's message (the first note after it,
 * unless a batch starts in between). ``liveKey`` names the
 * batch still running (its span stays open).
 */
export function foldRunRows<T extends { at: number }>(
  items: readonly T[],
  batches: readonly RunBatch[],
  probe: (item: T) => FoldProbe,
  liveKey: string | null = null,
): { kept: T[]; folded: Map<string, T[]> } {
  const kept: T[] = [];
  const folded = new Map<string, T[]>();
  const ordered = items
    .map((item, i) => ({ item, i, p: probe(item) }))
    .sort((a, b) => a.item.at - b.item.at || a.i - b.i);
  const writerTimes = ordered.filter((x) => x.p.role === "writer").map((x) => x.item.at);
  const ends = batchSpanEnds(batches, liveKey, writerTimes);
  let inReply = false;
  let replySince = 0;
  for (const { item, p } of ordered) {
    const { role, runId } = p;
    // A batch starting after the writer's message ends the wait for a reply:
    // what follows is the run's, not an answer.
    if (inReply && batches.some((b) => b.startAt > replySince && b.startAt <= item.at)) inReply = false;
    if (role === "writer") {
      inReply = true;
      replySince = item.at;
      kept.push(item);
      continue;
    }
    if (role === "reply") {
      inReply = false;
      kept.push(item);
      continue;
    }
    if (role === "other") {
      kept.push(item);
      continue;
    }
    if (inReply) {
      // Tool rows inside a reply belong to it; its first note answers it.
      if (role === "note") inReply = false;
      kept.push(item);
      continue;
    }
    const batch = batchForRow(item.at, runId, batches, ends);
    if (!batch) {
      kept.push(item);
      continue;
    }
    const list = folded.get(batch.key);
    if (list) list.push(item);
    else folded.set(batch.key, [item]);
  }
  return { kept, folded };
}

/** One folded row as the card / ribbon shows it. */
export interface RunStep {
  key: string;
  at: number;
  kind: "note" | "search" | "tool";
  /** Tool label for ``tool`` steps (e.g. ``citation.swap``). */
  label?: string;
  text: string;
}

/** A folded chat message as a step: web searches by their query, other
 *  tool rows by name + summary, notes by their text. */
export function runStepFromMessage(key: string, message: ChatMessage): RunStep {
  if (message.role === "tool") {
    if (message.name === "internet_search") return { key, at: message.at, kind: "search", text: message.summary };
    return { key, at: message.at, kind: "tool", label: message.name, text: message.summary };
  }
  return { key, at: message.at, kind: "note", text: message.text };
}

/** Cut ``s`` at a word boundary to at most ``max`` chars, with an ellipsis. */
function clip(s: string, max: number): string {
  const clean = s.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max + 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max / 2 ? cut.slice(0, space) : clean.slice(0, max)).replace(/[\s,;:·-]+$/, "")}…`;
}

/** The live card's status line for its newest step. */
export function runStepStatusText(step: RunStep): string {
  if (step.kind === "search") return `Searching the web: '${clip(step.text, 24)}'`;
  if (step.kind === "tool") return step.text ? `${step.label}: ${clip(step.text, 48)}` : (step.label ?? "");
  return clip(step.text, 80);
}
